import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Mail, Phone, Briefcase, Calendar } from "lucide-react";

export default function MyProfile() {
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [payroll, setPayroll] = useState([]);

  useEffect(() => {
    if (!user) return;
    // Find employee by email match
    api.get("/employees", { params: { q: user.email } }).then((r) => {
      const found = r.data.find((e) => e.email === user.email);
      if (found) {
        setEmp(found);
        api.get("/leaves", { params: { employee_id: found.id } }).then((x) => setLeaves(x.data));
        api.get("/payroll", { params: { employee_id: found.id } }).then((x) => setPayroll(x.data));
      } else {
        setEmp(false);
      }
    });
  }, [user]);

  if (emp === null) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (emp === false) return (
    <div className="card-flat text-center">
      <h2 className="font-heading text-xl font-bold">Welcome, {user?.name}</h2>
      <p className="text-stone-600 mt-2 text-sm">No linked employee record yet. Check the <Link to="/employees" className="text-primary underline">directory</Link>.</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="my-profile">
      <header>
        <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Self Service</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">My Profile</h1>
      </header>

      <div className="card-flat flex items-center gap-6 flex-wrap">
        <img src={emp.photo_url} alt="" className="w-20 h-20 rounded-full object-cover ring-4 ring-primary/10" />
        <div className="flex-1 min-w-[200px]">
          <div className="kpi-label">{emp.employee_code}</div>
          <h2 className="font-heading text-2xl font-bold tracking-tight mt-1">{emp.full_name}</h2>
          <div className="text-stone-600">{emp.designation} · {emp.department_name}</div>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-stone-500" />{emp.email}</div>
          <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-stone-500" />{emp.mobile}</div>
          <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-stone-500" />Joined {emp.joining_date}</div>
          <div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-stone-500" />{emp.employment_type}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-flat">
          <div className="kpi-label">My Leaves</div>
          <h3 className="font-heading text-xl font-bold mt-1 mb-3">Recent</h3>
          <div className="space-y-2" data-testid="my-leaves">
            {leaves.length === 0 && <div className="text-sm text-stone-500">No leaves yet</div>}
            {leaves.slice(0, 5).map((l) => (
              <div key={l.id} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                <div>
                  <div className="text-sm font-medium">{l.leave_type} · {l.days}d</div>
                  <div className="text-xs text-stone-500">{l.from_date} → {l.to_date}</div>
                </div>
                <span className="status-pill capitalize" style={{ background: l.status === "approved" ? "var(--color-primary-light)" : l.status === "rejected" ? "#FADED7" : "#FCE7C9", color: l.status === "approved" ? "var(--color-primary)" : l.status === "rejected" ? "#9A3412" : "#9A6020" }}>{l.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-flat">
          <div className="kpi-label">My Payslips</div>
          <h3 className="font-heading text-xl font-bold mt-1 mb-3">Recent</h3>
          <div className="space-y-2" data-testid="my-payroll">
            {payroll.length === 0 && <div className="text-sm text-stone-500">No payslips</div>}
            {payroll.slice(0, 6).map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                <div>
                  <div className="text-sm font-medium">{p.month}</div>
                  <div className="text-xs text-stone-500">Gross {formatINR(p.gross)} · Net {formatINR(p.net_pay)}</div>
                </div>
                <span className="status-pill capitalize" style={{ background: "var(--color-primary-light)", color: "var(--color-primary)" }}>{p.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
